import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import ModelLoader from '../ModelLoader';
import assets from '../assets';
import { Box3, Material, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry } from 'three';
import { GenericAsset } from '../domain';
import { zip } from '../utils';

const ml = ModelLoader.getInstance();

// this isn't really invisible, it's just not shown because the hit rectangle
// is not shown.
const invisibleMaterial = new MeshBasicMaterial({
    color: 0x00FF00
});

export default class GenericAssetView extends BaseView {

    declare model: GenericAsset;
    root: Object3D;
    hit: Mesh;
    hitBox: Box3 = new Box3();

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);

        this.name = 'Generic Asset';
        this.userData.isGenericAsset = true;
        this.userData.suppressExport = true;

        this.init();
    }

    make() {

        this.hit = new Mesh(new PlaneGeometry(), invisibleMaterial);
        this.hit.visible = false;
        this.hit.userData.isSelectable = true;
        this.hit.userData.isMovable = true;
        this.add(this.hit);

        this.setGlbMaterialOpacity = this.setGlbMaterialOpacity.bind(this);
    }

    bind() {
        const updateAppearance = this.updateAppearance.bind(this);
        this.model.bind('assetId', updateAppearance);
        this.model.bind('filename', updateAppearance);
        this.model.on('width', updateAppearance);
    }

    updateAppearance() {
        this.updates.queue(
            () => this.loadAsset(this.model),
            (object: Object3D) => {
                this.highlights.clear();
                object.traverse(obj => {
                    if(obj instanceof Mesh) {
                        this.highlights.add(obj);
                    }
                });
                this.root?.removeFromParent();
                this.root = object;
                this.add(this.root);

                this.hitBox.makeEmpty();
                this.hitBox.expandByObject(this.root);

                const width = this.hitBox.max.x - this.hitBox.min.x;
                const height = this.hitBox.max.y - this.hitBox.min.y;

                // Make sure that the origin is on the bottom edge of the object
                // TODO: this doesn't work consistently for various origin
                // placements, possibly because that hit box is in the global
                // reference frame.
                //this.root.position.y = -this.hitBox.min.y;

                this.hit.scale.set(width, height, 1);
                this.hit.position.set(0, height/2, 12);

                this.pipelineUpdate();
            });
    }

    /**
     * Loads and sets up materials for generic assets.
     */
    async loadAsset(asset: GenericAsset) {
        let filename: string;
        if(asset.assetId) {
            filename = assets.resolve(asset.assetId, "LowResModel");
        }
        else {
            // old way of getting filename, now deprecated in favor of the
            // assetId field
            filename = asset.filename;
        }
        if(filename.indexOf('#') != -1) {
            const target = this.model.width;
            let closest = 0
            for(const width of this.model.availableWidths) {
                if(width > closest && width <= target) {
                    closest = width;
                }
            }
            // fill in the default value if it's missing somehow
            if(closest == 0) {
                closest = this.model.availableWidths?.length > 0 ?
                    Math.min(...this.model.availableWidths)
                    : 24;
            }
            filename = filename.replace('#', String(closest));
        }
        const object = await ml.get(filename);
        const suffix = filename.split('.').pop().toLowerCase();

        if(asset.filename && asset.filename.indexOf('RodPD.obj') != -1) {
            for(const [child, material] of zip(object.children, asset.materials)) {
                this.bindMaterial(child as Mesh, material);
                ((child as Mesh).material as Material).needsUpdate = true;
            }
        }
        else if(suffix === "obj") {

            object.traverse(child => {
                if(child instanceof Mesh && child.geometry) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    if (asset.materials.length > 1) {
                        this.bindMaterialArray(child, asset.materials);
                    } else {
                        this.bindMaterial(child, asset.materials[0]);
                    }
                    child.material.needsUpdate = true;
                }
            });

        }
        else if(suffix === "glb"
                  && asset.materials
                  && asset.materials.length > 0)
        {
            const children = [];
            object.traverse(child => {
                if(child instanceof Mesh && child.geometry) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    children.push(child);
                }
            });
            for(let [obj, mat] of zip(children, asset.materials)) {
                this.bindMaterial(obj, mat);
            }
        }
        else if(suffix == "glb") {
            asset.off('opacity', this.setGlbMaterialOpacity);
            asset.on('opacity', this.setGlbMaterialOpacity);
        }

        return object;
    };

    setGlbMaterialOpacity() {
        this.root.traverse(obj => {
            if(obj instanceof Mesh) {
                obj.material.transparent = true;
                obj.material.opacity = this.model.opacity;
            }
        });
    }

}

